import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Prop } from '@stencil/core';
import { RoundButtonElevation, RoundButtonSize } from './round-button.types';

// Components
import { TimerAnimation } from './timer-animation/timer-animation';

/**
 * Circular shape buttons with icon that behaves same as regular buttons.
 *
 * @slot - Mandatory label and wb-icon placed within the element
 */
@Component({
  tag: 'wb-round-button',
  styleUrl: './round-button.scss',
  shadow: true,
})
export class RoundButtonComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** Size of the round button */
  @Prop({ reflect: true })
  public size!: RoundButtonSize;

  /** Elevation size consisting of border and shadow. */
  @Prop({ reflect: true })
  public elevation?: RoundButtonElevation;

  /** Duration in milliseconds for timer animation */
  @Prop({ reflect: true })
  public duration?: number;

  /** Pause the timer animation */
  @Prop({ reflect: true })
  public pause: boolean = false;

  /** button-aria-label should be provided for accessibility reasons and it will be used in the correspondent element */
  @Prop({ reflect: true })
  public buttonAriaLabel!: string;

  /** Shows the button as disabled and prevents interactions */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** Allow focus when it's disabled */
  @Prop({ reflect: true })
  public focusableWhenDisabled: boolean = false;

  /** Renders an anchor tag linked to the passed in URL (fragment) */
  @Prop()
  public href: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public download: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public rel: string | undefined;

  /** Can be used in combination with `href` property */
  @Prop()
  public target: string | undefined;

  /** Emitted when the button receives focus */
  @Event()
  public wbfocus!: EventEmitter<void>;

  /** Emitted when the button loses focus */
  @Event()
  public wbblur!: EventEmitter<void>;

  private onFocus = () => this.wbfocus.emit();

  private onBlur = () => this.wbblur.emit();

  private onClick = (event: MouseEvent) => {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
  };

  public render(): JSX.Element {
    const shouldDisabledBeFocusable = this.disabled && this.focusableWhenDisabled;
    const shouldDisabledNotBeFocusable = this.disabled && !this.focusableWhenDisabled;

    return (
      <Host //
        class="wb-round-button"
       
      >
        {this.href ? (
          <a //
            class="button"
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onClick={this.onClick}
            href={this.href}
            rel={this.rel}
            target={this.target}
            download={this.download}
            aria-label={this.buttonAriaLabel}
          >
            <slot />
            {this.duration && <TimerAnimation duration={this.duration} pause={this.pause} />}
          </a>
        ) : (
          <button //
            class="button"
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onClick={this.onClick}
            type="button"
            disabled={shouldDisabledNotBeFocusable}
            aria-disabled={shouldDisabledBeFocusable ? 'true' : 'false'}
            tabindex={shouldDisabledBeFocusable ? '0' : undefined}
            aria-label={this.buttonAriaLabel}
          >
            <slot />
            {this.duration && <TimerAnimation duration={this.duration} pause={this.pause} />}
          </button>
        )}
      </Host>
    );
  }
}
